import { useState, useCallback } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { movingItemApi } from '@/entities/moving-item';
import type { Spec } from '@/entities/moving-item/model/moving-item.types';

const SWR_KEY_BASE = 'moving-item-specs' as const;

export interface UseMovingItemSpecsReturn {
  specs: Spec[];
  isLoading: boolean;
  error: Error | undefined;
  refetch: () => void;
  search: (keyword: string, attributeKey: string) => void;
  createSpec: (spec: Omit<Spec, 'id'>) => Promise<Spec>;
  updateSpec: (specId: string, spec: Omit<Spec, 'id'>) => Promise<Spec>;
}

export function useMovingItemSpecs(): UseMovingItemSpecsReturn {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchAttributeKey, setSearchAttributeKey] = useState('');
  const { mutate } = useSWRConfig();
  const swrKey = [SWR_KEY_BASE, searchKeyword, searchAttributeKey] as const;
  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSWR<Spec[]>(
    swrKey,
    () =>
      movingItemApi.fetchSpecs({
        keyword: searchKeyword || undefined,
        attributeKey: searchAttributeKey || undefined,
      }),
    {
      revalidateOnMount: true,
      dedupingInterval: 10000,
    }
  );

  const search = useCallback((keyword: string, attributeKey: string) => {
    setSearchKeyword(keyword);
    setSearchAttributeKey(attributeKey);
  }, []);

  const refetch = useCallback(() => {
    revalidate();
  }, [revalidate]);

  const createSpec = async (spec: Omit<Spec, 'id'>): Promise<Spec> => {
    const created = await movingItemApi.createSpec(spec);
    await mutate(swrKey, (prev: Spec[] | undefined) => (prev ? [...prev, created] : [created]));
    return created;
  };

  const updateSpec = async (specId: string, spec: Omit<Spec, 'id'>): Promise<Spec> => {
    const updated = await movingItemApi.updateSpec(specId, spec);
    await mutate(swrKey, (prev: Spec[] | undefined) =>
      prev ? prev.map((s) => (s.id === specId ? updated : s)) : [updated]
    );
    return updated;
  };

  return {
    specs: data ?? [],
    isLoading,
    error: error ? new Error(error.message) : undefined,
    refetch,
    search,
    createSpec,
    updateSpec,
  };
}
